import type { Shipment } from "@/api.types.v2";
import type {
  UpdateShipmentSchema,
  shipmentPackageSchema,
} from "@/api.schemas";
import { useSession } from "@/services/auth0/session";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import type { z } from "zod";

const API_URL = import.meta.env.VITE_API_URL;

// Get all shipments
export function useShipments(params?: {
  orderId?: string;
  status?: string;
  carrier?: string;
  carrierIds?: string[];
  search?: string;
  limit?: number;
  offset?: number;
  include?: string[];
}) {
  const { token } = useSession();

  return useQuery({
    queryKey: ["shipments", params],
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (params?.orderId) queryParams.append("orderId", params.orderId);
      if (params?.status) queryParams.append("status", params.status);
      if (params?.carrier) queryParams.append("carrier", params.carrier);
      if (params?.carrierIds)
        queryParams.append("carrierIds", params.carrierIds.join(","));
      if (params?.search) queryParams.append("search", params.search);
      if (params?.limit) queryParams.append("limit", params.limit.toString());
      if (params?.offset)
        queryParams.append("offset", params.offset.toString());
      if (params?.include)
        queryParams.append("include", params.include.join(","));

      const { data } = await axios.get<{
        shipments: Shipment[];
        pagination: { total: number; limit: number; offset: number };
      }>(`${API_URL}/api/v2/shipments?${queryParams}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Transform to expected format
      return {
        data: data.shipments || [],
        metadata: {
          total: data.pagination?.total || 0,
        },
      };
    },
    enabled: !!token,
  });
}

// Get shipment by ID
export function useShipmentById(shipmentId: string | null) {
  const { token } = useSession();

  return useQuery({
    queryKey: ["shipment", shipmentId],
    queryFn: async () => {
      const { data } = await axios.get<Shipment>(
        `${API_URL}/api/v2/shipments/${shipmentId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return data;
    },
    enabled: !!token && !!shipmentId,
  });
}

// Update shipment
export function useUpdateShipment(
  shipmentId: string,
  options?: {
    onSuccess?: (data: Shipment) => void;
    onError?: (error: Error) => void;
  }
) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updateData: UpdateShipmentSchema) => {
      const { data } = await axios.patch<{
        data: Shipment;
      }>(`${API_URL}/api/v2/shipments/${shipmentId}`, updateData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return data.data;
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: ["shipment", shipmentId],
      });
      await queryClient.invalidateQueries({ queryKey: ["shipments"] });
      if (data.orderId) {
        await queryClient.invalidateQueries({
          queryKey: ["order", data.orderId],
        });
        await queryClient.invalidateQueries({ queryKey: ["orders"] });
      }
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

// Update shipment status
export function useUpdateShipmentStatus(
  shipmentId: string,
  options?: {
    onSuccess?: (data: Shipment) => void;
    onError?: (error: Error) => void;
  }
) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (status: string) => {
      const { data } = await axios.patch<{
        data: Shipment;
      }>(
        `${API_URL}/api/v2/shipments/${shipmentId}/status`,
        { status },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return data.data;
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: ["shipment", shipmentId],
      });
      await queryClient.invalidateQueries({ queryKey: ["shipments"] });
      if (data.orderId) {
        await queryClient.invalidateQueries({
          queryKey: ["order", data.orderId],
        });
        await queryClient.invalidateQueries({ queryKey: ["orders"] });
      }
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

// Update shipment packages
export function useUpdateShipmentPackages(
  shipmentId: string,
  options?: {
    onSuccess?: (data: Shipment) => void;
    onError?: (error: Error) => void;
  }
) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (packages: z.infer<typeof shipmentPackageSchema>[]) => {
      const { data } = await axios.put<{
        data: Shipment;
      }>(
        `${API_URL}/api/v2/shipments/${shipmentId}/packages`,
        { packages },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return data.data;
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: ["shipment", shipmentId],
      });
      await queryClient.invalidateQueries({ queryKey: ["shipments"] });
      if (data.orderId) {
        await queryClient.invalidateQueries({
          queryKey: ["order", data.orderId],
        });
        await queryClient.invalidateQueries({ queryKey: ["orders"] });
      }
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

// Get shipment label
export function useShipmentLabel(shipmentId: string) {
  const { token } = useSession();

  return useQuery({
    queryKey: ["shipment-label", shipmentId],
    queryFn: async () => {
      const { data } = await axios.get<{
        data: {
          labelUrl: string;
          trackingNumber: string;
          carrierCode: string;
          serviceCode: string;
        };
      }>(`${API_URL}/api/v2/shipments/${shipmentId}/label`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return data.data;
    },
    enabled: !!token && !!shipmentId,
  });
}

// Get shipping rates for a shipment
export function useShipmentRates(shipmentId: string) {
  const { token } = useSession();

  return useQuery({
    queryKey: ["shipment-rates", shipmentId],
    queryFn: async () => {
      const { data } = await axios.get<{
        data: {
          rates: {
            provider: string;
            carrierCode: string;
            serviceCode: string;
            serviceName: string;
            listRate: number;
            retailRate: number;
            currency: string;
            estimatedDays: number;
            estimatedDelivery: string;
          }[];
        };
      }>(`${API_URL}/api/v2/shipments/${shipmentId}/rates`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return data.data;
    },
    enabled: !!token && !!shipmentId,
  });
}

// =============================================================================
// Shipment Activity (Admin only)
// =============================================================================

export type ShipmentActivityType =
  | "STATUS_CHANGE"
  | "LABEL_EVENT"
  | "CARRIER_UPDATE"
  | "ADMIN_NOTE"
  | "RECIPIENT_REQUEST"
  | "SYSTEM_EVENT";

export type ActivityActorType = "SYSTEM" | "ADMIN" | "RECIPIENT" | "CARRIER";

export interface ShipmentActivity {
  id: string;
  shipmentId: string;
  type: ShipmentActivityType;
  action: string;
  description: string;
  metadata: Record<string, unknown> | null;
  actorType: ActivityActorType;
  actorId: string | null;
  createdAt: string;
}

// Get activity log for a shipment
export function useShipmentActivities(
  shipmentId: string,
  params?: {
    limit?: number;
    offset?: number;
    types?: ShipmentActivityType[];
  }
) {
  const { token } = useSession();

  return useQuery({
    queryKey: ["shipment-activities", shipmentId, params],
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (params?.limit) queryParams.append("limit", params.limit.toString());
      if (params?.offset)
        queryParams.append("offset", params.offset.toString());
      if (params?.types) queryParams.append("types", params.types.join(","));

      const { data } = await axios.get<{
        activities: ShipmentActivity[];
        pagination: { total: number; limit: number; offset: number };
      }>(
        `${API_URL}/api/v2/shipments/${shipmentId}/activities?${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return {
        activities: data.activities,
        pagination: data.pagination,
      };
    },
    enabled: !!token && !!shipmentId,
  });
}

// Add admin note to shipment
export function useAddShipmentNote(
  shipmentId: string,
  options?: {
    onSuccess?: (data: ShipmentActivity) => void;
    onError?: (error: unknown) => void;
  }
) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (note: string) => {
      const { data } = await axios.post<ShipmentActivity>(
        `${API_URL}/api/v2/shipments/${shipmentId}/activities/note`,
        { note },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return data;
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: ["shipment-activities", shipmentId],
      });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

/**
 * A label with its carrier's procurement answered as a boolean.
 *
 * `selfRated` decides whether EZX PRINTED this label (it prices the carrier from
 * its own tables, so there is no provider API to buy one from) or BOUGHT it. The
 * server answers it — no client re-derives the rule.
 */
export interface ShipmentLabel {
  id: string;
  shipmentId: string;
  packageId: string | null;
  carrierId: string;
  providerRef: string;
  trackingNumber: string;
  labelFormat: string;
  fileUrl: string | null;
  labelData: Buffer | null;
  purchasedAt: string;
  voidedAt: string | null;
  voidReason: string | null;
  carrier: {
    /** Priced from EZX's own rate tables — NOT "EZX's own fleet". */
    selfRated: boolean;
  };
}

// Get labels for a shipment (both internal and carrier labels)
export function useShipmentLabels(shipmentId: string) {
  const { token } = useSession();

  return useQuery({
    queryKey: ["shipment-labels", shipmentId],
    queryFn: async (): Promise<ShipmentLabel[]> => {
      const { data } = await axios.get<{
        data: ShipmentLabel[];
        count: number;
      }>(`${API_URL}/api/v2/shipping/shipments/${shipmentId}/labels`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return data.data;
    },
    enabled: !!token && !!shipmentId,
  });
}

// =============================================================================
// Shipment Cancellation Request
// =============================================================================

// Request cancellation (smart: immediate for PENDING_PAYMENT, pending for others)
export interface RequestCancellationParams {
  reason?: string;
}

export interface RequestCancellationResponse {
  success: boolean;
  message: string;
  immediate?: boolean; // true if cancelled immediately (PENDING_PAYMENT)
  pending?: boolean; // true if pending admin review (paid shipments)
  shipment: {
    id: string;
    shipmentNumber: number;
    status?: string; // present if immediate
    cancellationRequestedAt?: string; // present if pending
  };
  tasksCancelled?: number;
}

export function useRequestShipmentCancellation(options?: {
  onSuccess?: (data: RequestCancellationResponse) => void;
  onError?: (error: unknown) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      shipmentId,
      reason,
    }: RequestCancellationParams & { shipmentId: string }) => {
      const { data } = await axios.post<RequestCancellationResponse>(
        `${API_URL}/api/v2/shipments/${shipmentId}/request-cancellation`,
        { reason },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return data;
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["shipments"] });
      await queryClient.invalidateQueries({
        queryKey: ["pending-cancellations"],
      });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}
